import { endpoints } from '../../api';
import { ApiError, uuid } from '../../api/client';
import { UploadRejectedError, UploadTransportError } from '../../api/uploads';
import type { ClonePolicy, CreateUserVoice, UserVoice } from '../../api/user-voices';
import { cloneError, isDefiniteUploadRejection, voiceErrorText } from './clone-errors';
import { confirmAudioType } from './sample-file';
import { intentSlot, readIntent, submitIntent } from './clone-intent';

/** Свой голос в полях, которые читает раздел шаблона (engine.ts): id — Backend user voice ID, voiceId — ID голоса в каталоге Voice для выбора. */
export interface CloneVoice {
  id: number;
  voiceId: number | null;
  name: string;
  desc: string;
  st: 'processing' | 'ready' | 'failed';
  step: UserVoice['step'];
  err: string | null;
  errorText: string | null;
  verified: boolean;
  samples: number;
  dur: number;
  at: string;
  orbPair: [string, string] | null;
  uses: number | null;
  symbols: number | null;
  verificationState: string | null;
  providerState: string | null;
  previewState: string | null;
  generation: number;
  version: number;
  hasPreview: boolean;
  isS2s: boolean;
  /** Имена аватаров, которые говорят этим голосом (used_by_avatars); null — сервер их не отдал */
  usedBy: string[] | null;
}

export interface CloneList {
  voices: CloneVoice[];
  limit: number | null;
  policy: ClonePolicy | null;
  pollMs: number | null;
  used: number;
  entitlementVersion: string | null;
}

/** Образец мастера: поля шаблона плюс состояние загрузки, которое переживает повтор «Создать голос». */
export interface CloneSample {
  name: string;
  file?: File;
  blob?: Blob;
  rec?: boolean;
  bad?: string | null;
  uploadKey?: string;
  uploadId?: string;
  pendingId?: string;
}

export interface CloneDraft {
  name: string;
  desc: string;
  denoise: boolean;
  samples: CloneSample[];
  createKey?: string;
}

function toVoice(voice: UserVoice): CloneVoice {
  return {
    id: voice.id,
    voiceId: voice.voice_id,
    name: voice.name,
    desc: voice.description,
    st: voice.status === 'deleting' ? 'processing' : voice.status,
    step: voice.step,
    err: voice.error_code,
    errorText: voice.error_code ? voiceErrorText(voice.error_code) : null,
    verified: voice.verified,
    samples: voice.samples_count,
    dur: Math.round(voice.duration_ms / 1000),
    at: voice.created_at,
    orbPair: voice.orb ? [voice.orb.from, voice.orb.to] : null,
    uses: voice.stats?.uses ?? null,
    symbols: voice.stats?.symbols ?? null,
    verificationState: voice.verification_state,
    providerState: voice.provider_state,
    previewState: voice.preview_state,
    generation: voice.generation,
    version: voice.version,
    hasPreview: !!voice.preview_url,
    isS2s: voice.is_s2s,
    usedBy: voice.used_by_avatars ? voice.used_by_avatars.map(avatar => avatar.name).filter(Boolean) : null,
  };
}

/** Запись рекордера приходит Blob без имени: сервер принимает только поддерживаемый MIME. */
function sampleFile(sample: CloneSample): File {
  if (sample.file) return confirmAudioType(sample.file);
  const type = (sample.blob?.type || 'audio/webm').split(';')[0];
  const extension = type === 'audio/mp4' ? 'm4a' : type.split('/')[1] || 'webm';
  return confirmAudioType(new File([sample.blob!], `${sample.name}.${extension}`, { type }));
}

async function uploadSample(sample: CloneSample, consentVersion: string) {
  if (sample.uploadId) return;
  sample.uploadKey ??= uuid();
  try {
    const upload = sample.pendingId
      ? await endpoints.waitForUpload(sample.pendingId)
      : await endpoints.uploadFile(
          sampleFile(sample),
          { purpose: 'voice_sample', kind: 'audio', origin: sample.rec ? 'recording' : 'file', consentVersion, consentAccepted: true },
          sample.uploadKey,
        );
    sample.uploadId = upload.id;
    sample.pendingId = undefined;
  } catch (failure) {
    // Неопределённый PUT продолжаем опросом прежнего slot; явный отказ требует нового ключа.
    if (isDefiniteUploadRejection(failure) || (failure instanceof UploadRejectedError && failure.upload.status === 'expired')) {
      sample.pendingId = undefined;
      sample.uploadKey = uuid();
    } else if (failure instanceof UploadTransportError) sample.pendingId = failure.uploadId;
    else if (failure instanceof UploadRejectedError) sample.pendingId = undefined;
    throw failure;
  }
}

const invalidInput = new Set(['voice_name_invalid', 'voice_description_invalid', 'voice_invalid', 'samples_invalid', 'sample_invalid', 'sample_duration_invalid']);

export const cloneServer = {
  async catalog() {
    const [plans, subscription] = await Promise.all([endpoints.commerce.plans(), endpoints.commerce.subscription()]);
    return { plans, subscription };
  },

  async pendingCreate() {
    const intent = readIntent(await intentSlot('create'));
    return intent ? { key: intent.key, body: intent.body as CreateUserVoice } : null;
  },

  async resumeCreate() {
    const slot = await intentSlot('create'), pending = readIntent(slot);
    if (!pending) throw new Error('clone_intent_missing');
    return toVoice(await submitIntent(slot, pending, intent => endpoints.userVoices.create(intent.body as CreateUserVoice, intent.key)));
  },
  async list(): Promise<CloneList> {
    const page = await endpoints.userVoices.list();
    const items = [...page.items];
    const cursors = new Set<string>();
    let cursor = page.next_cursor;
    while (cursor) {
      if (cursors.has(cursor)) throw new Error('clone_cursor_repeated');
      cursors.add(cursor);
      const next = await endpoints.userVoices.list(cursor);
      items.push(...next.items);
      cursor = next.next_cursor;
    }
    return {
      voices: [...new Map(items.filter(item => item.status !== 'deleting').map(item => [item.id, toVoice(item)])).values()],
      limit: page.limit,
      policy: page.policy ?? null,
      pollMs: page.poll_after_ms,
      used: page.used,
      entitlementVersion: page.entitlement_version,
    };
  },

  async get(id: number) {
    return toVoice(await endpoints.userVoices.get(id));
  },

  /** Образцы грузятся по одному; ключ создания живёт в черновике, поэтому повтор после сбоя не создаёт второй голос. */
  async create(draft: CloneDraft, policy: ClonePolicy, onProgress: (done: number, total: number) => void) {
    const slot = await intentSlot('create');
    const pending = readIntent(slot);
    if (pending) return toVoice(await submitIntent(slot, pending, intent => endpoints.userVoices.create(intent.body as CreateUserVoice, intent.key)));
    if (!policy.creation_enabled) throw new ApiError(503, 'clone_creation_unavailable', 'clone_creation_unavailable');
    if (!policy.consent_version || !policy.consent_text) throw new ApiError(422, 'consent_required', 'consent_required');
    const name = draft.name.trim(), description = draft.desc.trim();
    if ([...name].length < 2 || [...name].length > 32)
      throw new ApiError(422, 'voice_name_invalid', 'voice_name_invalid');
    if ([...description].length > 120)
      throw new ApiError(422, 'voice_description_invalid', 'voice_description_invalid');
    const samples = draft.samples.filter(sample => !sample.bad);
    if (!samples.length || samples.length > policy.max_samples)
      throw new ApiError(422, 'samples_invalid', 'samples_invalid');
    for (const [index, sample] of samples.entries()) {
      onProgress(index, samples.length);
      await uploadSample(sample, policy.consent_version!);
    }
    onProgress(samples.length, samples.length);
    draft.createKey ??= uuid();
    try {
      const body: CreateUserVoice = {
          name,
          description,
          denoise: draft.denoise,
          consent_version: policy.consent_version!,
          samples: samples.map(sample => ({ upload_id: sample.uploadId!, source: sample.rec ? 'recording' : 'file', script_no: null })),
        };
      const voice = await submitIntent(slot, { version: 1, kind: 'create', key: draft.createKey, body },
        intent => endpoints.userVoices.create(intent.body as CreateUserVoice, intent.key));
      return toVoice(voice);
    } catch (failure) {
      // Только детерминированный отказ входных данных разрешает новый ключ создания.
      if (failure instanceof ApiError && failure.status === 422 && invalidInput.has(failure.code)) draft.createKey = undefined;
      throw failure;
    }
  },

  async retry(id: number, changes: { name?: string; desc?: string; denoise?: boolean }, expectedVersion: number,
    idempotencyKey: string, policy?: ClonePolicy) {
    const slot = await intentSlot('retry', id), pending = readIntent(slot);
    if (!pending) {
      if (policy && !policy.creation_enabled) throw new ApiError(503, 'clone_creation_unavailable', 'clone_creation_unavailable');
      const current = await endpoints.userVoices.get(id);
      if (current.status !== 'failed' || current.provider_state === 'uncertain' || current.error_code === 'provider_outcome_unknown')
        throw new ApiError(409, 'voice_not_retryable', 'voice_not_retryable');
    }
    const body: { name?: string; description?: string; denoise?: boolean } = {};
    if (changes.name != null) body.name = changes.name.trim();
    if (changes.desc != null) body.description = changes.desc.trim();
    if (changes.denoise != null) body.denoise = changes.denoise;
    return toVoice(await submitIntent(slot, { version: 1, kind: 'retry', key: idempotencyKey, body, id, expectedVersion },
      intent => endpoints.userVoices.retry(intent.id!, intent.body, intent.expectedVersion!, intent.key)));
  },

  async update(id: number, changes: { name?: string; desc?: string }, version: number) {
    const body: { name?: string; description?: string } = {};
    if (changes.name != null) body.name = changes.name;
    if (changes.desc != null) body.description = changes.desc;
    return toVoice(await endpoints.userVoices.update(id, body, version));
  },

  remove(id: number) {
    return endpoints.userVoices.remove(id);
  },

  /** Превью защищено Bearer: скачиваем Blob и отдаём локальный URL. */
  async previewUrl(id: number) {
    return URL.createObjectURL(await endpoints.userVoices.preview(id));
  },

  isConflict(failure: unknown) {
    return failure instanceof ApiError && failure.code === 'version_conflict';
  },

  message: cloneError,
};

export type CloneServer = typeof cloneServer;
